﻿import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import type { UUIDTypes } from "uuid";
import type { RootState } from "@/typedDispatch";

export const endpointTypesEnum = {
  SFTP: 0,
  FTPS: 1,
  RESTAPI: 2,
  SOAPAPI: 3,
  InternalDBTable: 4,
  VirtualPacking: 5,
  Email: 6,
};

export const endpointTypes = [
  { key: endpointTypesEnum.SFTP, label: "SFTP", isChecked: false },
  { key: endpointTypesEnum.FTPS, label: "FTPS", isChecked: false },
  { key: endpointTypesEnum.RESTAPI, label: "RESTAPI", isChecked: false },
  { key: endpointTypesEnum.SOAPAPI, label: "SOAPAPI", isChecked: false },
  {
    key: endpointTypesEnum.InternalDBTable,
    label: "InternalDBTable",
    isChecked: false,
  },
];

export const appTypesEnum = {
  RetailChannel: 0,
  ERP: 2,
  WMS: 3,
  CMS: 4,
};

export const appTypes = [
  {
    key: appTypesEnum.RetailChannel,
    label: "Retail Channel",
  },
  {
    key: appTypesEnum.ERP,
    label: "ERP",
  },
  {
    key: appTypesEnum.WMS,
    label: "WMS",
  },
  {
    key: appTypesEnum.CMS,
    label: "CMS",
  },
];

const standardTypesEnum = {
  X12EDI: 1,
  JSON: 2,
  FlatFile: 3,
  XML: 4,
  CSV: 5,
  ProprietaryDataStream: 6,
};

export const standardTypes = [
  { key: standardTypesEnum.X12EDI, label: "X12EDI", isChecked: false },
  { key: standardTypesEnum.JSON, label: "JSON", isChecked: false },
  { key: standardTypesEnum.FlatFile, label: "FlatFile", isChecked: false },
  { key: standardTypesEnum.XML, label: "XML", isChecked: false },
  {
    key: standardTypesEnum.CSV,
    label: "CSV",
    isChecked: false,
  },
  {
    key: standardTypesEnum.ProprietaryDataStream,
    label: "ProprietaryDataStream",
    isChecked: false,
  },
];

const ConnectionTypesEnum = {
  Customer: 0,
  ERP: 1,
  Warehouse: 2,
};

export interface IConnectionType {
  key: number;
  label: string;
  isChecked: boolean;
}
export const connectionTypes: IConnectionType[] = [
  { key: ConnectionTypesEnum.Customer, label: "Customer", isChecked: false },
  { key: ConnectionTypesEnum.ERP, label: "ERP", isChecked: false },
  { key: ConnectionTypesEnum.Warehouse, label: "Warehouse", isChecked: false },
];

export const FileInStatusEnum = {
  New: 0,
  New_from_UI: 1,
  Retranslating: 5,
  Translated: 10,
  Skipped: 20,
  Invalid: 30,
  Error_at_Re_translating: 40,
  Cancelled: 50,
};

export const PendingArtifactStatusEnum = {
  New: 0,
  ErrorInTranslation: 5,
  AwaitingConfirmation: 20,
  Rejected: 21,
  Confirmed: 22,
  Completed: 30,
};

const defaultNewConnection = {
  model: {
    name: "",
    type: null,
    workflows: [],
    targetConnections: [],
    schemaLibrary: [],
    url: null,
  },
  connectionNames: {
    records: [],
    recordsLoaded: true, // standardize naming
    loadingError: "",
  },
  schemaLibrary: {
    records: [],
    recordsLoaded: true,
    loadingError: "",
    filters: {
      search: "",
    },
  },
};
const defaultEditConnection: IEditConnectionState = {
  allConnections: [],
  allConnectionsLoaded: true,
  operations: [],
  operationsLoaded: true,
  endpoints: [],
  endpointsLoaded: true,
  selectedPairs: [],
  connectionPairs: [],
  connectionPairsLoaded: true,
  connectionPairWorkflow: {},
  open: false,
  id: null,
  loadingError: null,
  connectionProfiles: [],
  connectionProfilesLoaded: false,
  operationsError: null,
  connectionProfilesError: null,
  modifiedConnectionPairs: [],
  model: {}
};

const defaultEditSchema = {
  open: false,
};

export const OperationProcessState = {
  InQueue: 0,
  AllocatingWorker: 10,
  InProgress: 20,
  Paused: 80,
  Cancelled: 90,
  Completed: 100,
  CompletedWithWarning: 101,
  FailedWithError: 110,
  FailedToBeReattempted: 111,
  Hold: 15,
  Archive: 120,
};

export const connEndpointStatus: IEndpointStatusFilter[] = [
  {
    key: OperationProcessState.Completed,
    values: [OperationProcessState.Completed, OperationProcessState.CompletedWithWarning],
    label: "Connected",
    isChecked: false,
  },
  {
    key: OperationProcessState.FailedWithError,
    values: [OperationProcessState.FailedWithError, OperationProcessState.FailedToBeReattempted],
    label: "Failed",
    isChecked: false,
  },
];

export const defaultCode = "[DEFAULT]";
export const blankCode = "[BLANK]";
export const defaultMinimumAttachmentSizeKb = 20;

interface ITabSelectionState {
  integration: number;
  connections: number;
  endpoints: number;
  //disabled
  //newRequestConnection: number;
  fileManager: number;
}

interface IEmailSettingsState {
  EmailAdresses: string[];
  UseDomainMatch: boolean;
  Domain: string;
}

interface ICustomField {
  FieldId: UUIDTypes;
  FieldValue: string;
}
interface IConnectionState {
  ConnectionId: number;
  ConnectionName: string;
  EndpointId: number;
  SchemaCollectionId: number;
  IsDisabled: boolean;
  // unused
  //LegacyType: unknown;
  //LegacyId: unknown;
  JSONProperties: string;
  ShortFormId: string;
  DisplayName: string;
  ConnectionType: number;
  EmailSettings: IEmailSettingsState;
  CustomFieldValues: ICustomField[];
  /***
   * fields carried over from the backend, should be sanitized
      ArtifactDictionaryDatas(pin):
      ConnectionArtifactSchemas(pin):
      ConnectionPairs_ConnectionId1(pin):
      ConnectionPairs_ConnectionId2(pin):
      EndpointsProcessLogs(pin):
      FileIns(pin):
      FileOuts(pin):
      OperationsProcessLogs(pin):
      RecordGroupLinks(pin):
      VPCustomerSettings(pin):
      VPPackingShipments(pin):
      PendingArtifacts(pin):null
      Endpoint(pin):null
      SchemaCollection(pin):null
   */
}

interface IOperationState {
  OperationId: number;
  OperationName: string;
  SourceConnections: number[];
  TargetConnections: number[];
}
interface IEndpointState {
  id: string;
  AppTypeLabel: string;
  ProtocolLabel: string;
  EndpointId: number;
  EndpointName: string;
  AppType: number;
  ProtocolId: number;
  ProtocolParamsJSON: string;
  IsDisabled: boolean;
  IsDeleted: boolean;
  /**
   * unused fields to sanitize:
      Connections(pin):
      EndpointsProcessLogs(pin):
   */
}
interface IConnectionProfileState {
  ConnectionProfileId: number;
  OperationId: number;
  ConnectionPairId: 1;
  GlobalProfileId: UUIDTypes | null;
  JSONProperties: string;
  JSONVersion: string | null;
  /**
   * unused fields to sanitize:
      ConnectionPair(pin):null
      GlobalProfile(pin):null
   */
}
interface IConnectionPairState {
  id: string;
  ConnectionName1: string;
  ConnectionName2: string;
  ConnectionType1: number;
  ConnectionType2: number;
  ConnectionPairId: number;
  ConnectionPairName: string;
  ConnectionId1: number;
  ConnectionId2: number;
  IsDeleted: boolean;
  JSONProperties: string;
  JSONVersion: string | null;
  ConnectionProfiles: IConnectionProfileState[];
  /**
   * unused fields to sanitize:
      CrossReferenceMappings(pin): []
      Connection_ConnectionId1(pin):null
      Connection_ConnectionId2(pin):null
   */
}

interface IConnectionPairWorkflowRecord {
  operationId: number;
  operationName: string;
  sourceConnectionId: number;
  targetConnectionId: number;
}
interface IConnectionPairWorkflowState {
  [key: number]: IConnectionPairWorkflowRecord[];
}
interface IEditSchemaState {
  open: boolean;
}
export interface IConnectionSummaryRecord {
  ConnectionId: number;
  ConnectionName: string;
  JSONProperties: string;
  EndpointId: number;
  EndpointType: number;
  EndpointName: string;
  SchemaCollectionId: number;
  SchemaCollectionName: string;
  IsDisabled: boolean;
  DisplayName: string;
  ConnectionType: number;
  Status: number;
  JSONAuditLog: string;
  LogTimeUTC: string;
  LastUpdated: string;
  id: number;
  StatusMessage: string;
}

export interface IEndpointRecord {
  Id: number;
  Status?: number;
  AppTypeLabel: string;
  Type: string;
  ConnectionId: number;
  Number: number;
  LogTimeUTC: string;
  LastUpdated: string;
  EndpointId: number;
  EndpointName: string;
  AppType: number;
  ProtocolId: number;
  ProtocolParamsJson: string;
  IsDisabled: boolean;
  IsDeleted: boolean;
  statusMessage: string;
  // unused fields (sanitize)
  // Connections: unknown[];
  // EndpointsProcessLogs: unknown[];
}
// largely duplicated with connection pair in edit connection
interface IConnectionPairRecord {
  ConnectionPairId: number;
  ConnectionPairName: string;
  ConnectionName1: string;
  Connection_1_Type: number;
  Connection_1_EndpointName: string;
  ConnectionName2: string;
  Connection_2_Type: number;
  Connection_2_EndpointName: string;
}
interface IFilter {
  key: number;
  label: string;
  isChecked: boolean;
}
// parameters should be standardized to be in line woth IFilter interface
interface IManagerFilter {
  Value: number;
  Label: string;
  isChecked?: boolean;
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface IFilters {
  // base filters type
}
interface IManagerFilters {
  status: IManagerFilter[];
}
interface ISummaryFilters extends IFilters {
  status: IFilter[];
  type: IFilter[];
}
export interface IEndpointStatusFilter extends IFilter {
  values: number[];
}
interface IEndpointSummaryFilters extends IFilters {
  status: IEndpointStatusFilter[];
  type: IFilter[];
}
interface ISearchFilter extends IFilters {
  search: string;
}
interface IEndpointErrorRatesByEndpointId {
  [key: number]: IEndpointErrorRate;
}
interface IConnectionSummaryErrorRatesByConnectionId {
  [key: number]: IConnectionSummaryErrorRate;
}
interface IEndpointSummaryState extends IRecordStateFiltered<IEndpointRecord, IEndpointSummaryFilters> {
  errorRates: IEndpointErrorRatesByEndpointId,
  loadingErrorRatesError: string | null;
  errorRatesLoaded: boolean;
}
interface IConnectionSummaryState extends IRecordStateFiltered<IConnectionSummaryRecord, ISummaryFilters> {
  triggerRefresh: boolean;
  loadingError: string | null;
  errorRates: IConnectionSummaryErrorRatesByConnectionId,
  loadingErrorRatesError: string | null;
  errorRatesLoaded: boolean;
}
interface IRecordsState<T> {
  records: T[];
  recordsLoaded: boolean;
  loadingError: string | null;
}
interface IRecordState<T> {
  record: T | null;
  recordLoaded: boolean;
  loadingError: string | null;
}
interface IRecordStateFiltered<T, F> extends IRecordsState<T> {
    filters: F;
}

interface ISchemaRecord {
  id: string;
  TemplateId: number;
  TradingPartnerId: number
  TradingPartnerName: string;
  Address1: string;
  Address2: string;
  Address3: string;
  Address4: string;
  City: string;
  State: string;
  Country: string;
  PostalCode: string;
  Contact: string;
  Phone: string;
  Fax: string;
  Email: string;
  Notes: string;
  Comments: string;
  ISAId: string;
  TISAId: string;
  ISA01: string;
  ISA02: string;
  ISA03: string;
  ISA04: string;
  ISAIdQ: string;
  ISA11: string;
  ISA12: string;
  ISA16: string;
  GS03: string;
  TGS03: string;
  GS07: string;
  GS08: string;
  Separator: string;
  Ierminator: string;
  CreateDate: string;
  ModifyDate: string;
}
export interface ICustomMatchRow {
    [key: string]: number | string;
    RowId: number;
    Conditional: number;
    Bracket: number;
    ElementName: string;
    Position: number;
    Equivalency: number;
    Value: string;
}
export interface IConnectionSettingsRecord {
  [key: string]: string| number | boolean | null | ICustomMatchRow[];
  TestCustomConnectionMatch: ICustomMatchRow[];
  ProdCustomConnectionMatch: ICustomMatchRow[];
  SenderISACodeQualifier: string;
  SenderISACode: string;
  SenderISATestCodeQualifier: string;
  SenderISATestCode: string;
  ReceiverISACodeQualifier: string;
  ReceiverISACode: string;
  SenderGSCode: string;
  SenderGSTestCode: string;
  ReceiverGSCode: string;
  ReceiverISATestCodeQualifier: string;
  ReceiverISATestCode: string;
  ReceiverGSTestCode: string;
  ISA01: string;
  ISA02: string;
  ISA03: string;
  ISA04: string;
  ISA11: string;
  ISA12: string;
  ISA14: string;
  ISA15: string;
  ISA16: string;
  GS07: string;
  GS08: string;
  SegmentTerminator: string;
  ElementSeparator: string;
  MatchPattern: string | null;
  IsInboundAcknowledgementRequired: boolean;
  MaximumTimeoutInHours: number;
  OutboundAcknowledgementSetting: number;
  IsISAGSOptionalFieldRequired: boolean;
}
interface IReadOptionsRecord {
  ShipToQualifier: string | null;
  ShipFromQualifier: string | null;
  BillToQualifier: string | null;
  DTSegmentId: string | null;
  DTQualifierPosition: string | null;
  ShipDateQualifier: string | null;
  DeliveryDateQualifier: string | null;
  CancelDateQualifier: string | null;
  ReceiveDateQualifier: string | null;
}
interface IReadOptionsState {
  record: IReadOptionsRecord;
  recordLoaded: boolean;
  loadingError: string | null;
}
export interface IConnectionRecord {
  Id: number;
  ConnectionId: number;
  Name: string;
  Endpoint: string;
  SchemaSet: string;
  JSONProperties: string;
  DisplayName: string;
  Type: number;
}
// connectionNames are accessed by the catalog grid, everything else is unused
interface IRequestNewConnectionState {
  model: {
    name: string;
    type: string | null;
    workflows: never[];
    targetConnections: never[];
    schemaLibrary: never[];
    url: string | null;
  };
  connectionNames: IRecordsState<IConnectionRecord>;
  schemaLibrary: IRecordStateFiltered<never, ISearchFilter>;
}
interface ISchemaCollectionAddedState {
  SchemaCollectionId: number;
  connectionId: number;
}
interface ISchemaDocumentRecord {
  EndpointSchemaId: number;
  SchemaCollectionId: number;
  DocumentName: string;
  DocumentDescriptipn: string;
  ArtifactType: number;
  SchemaCollectionName: string;
  SchemaType: string;
  Direction: string;
}
export interface ISchemaCollectionRecord {
  SchemaCollectionName: string;
  SchemaType: number;
  SchemaName: string;
  EndpointSchemaId: number;
  ConnectionId: number;
  EndpointId: number;
  SchemaCollectionId: number;
  ArtifactType: number;
  JSONProperties: string;
  Standard: string;
  Name: string;
  Document: string;
  DocumentDescription: string;
  IsTransactionSettings: boolean;
  Direction: string;
  VariantName: string;
  ConnectionArtifactSchemaId: string;
}

interface ISharedCatalogRecord {
  PrimaryConnectionId: number;
  SecondaryConnectionId: number;
  SelectedCatalogs: number[];
  ConnectionId1: number;
  ConnectionId2: number;
}
interface IConnectionPairBriefRecord {
  ConnectionId1: number;
  ConnectionId2: number;
  ConnectionPairName: string;
  ConnectionPairId: number;
  Status: number;
}

interface IEditConnectionState {
  allConnections: IConnectionState[];
  allConnectionsLoaded: boolean;
  operations: IOperationState[];
  operationsLoaded: boolean;
  operationsError: string | null;
  endpoints: IEndpointState[];
  endpointsLoaded: boolean;
  connectionPairs: IConnectionPairState[];
  connectionPairsLoaded: boolean;
  selectedPairs: never[]; // appears unused
  connectionProfiles: IConnectionProfileState[];
  connectionProfilesLoaded: boolean;
  connectionProfilesError: string | null;
  // to be continued
  connectionPairWorkflow: IConnectionPairWorkflowState;
  open: boolean;
  model: any;
  modifiedConnectionPairs: never[];
  id: number | null;
  loadingError: string | null;
}
export interface ISelectedConnection {
  ConnectionId: number;
  ConnectionName: string;
}
interface IIntegrationSliceState {
  tabSelections: ITabSelectionState;
  requestNewConnection: IRequestNewConnectionState; // connectionNames accessed by the catalog grid, doesn't seem right
  editConnection: IEditConnectionState;
  editSchema: IEditSchemaState;
  connectionSummary: IConnectionSummaryState;
  endpointSummary: IEndpointSummaryState;
  connectionPair: IRecordsState<IConnectionPairRecord>;
  fileManager: IRecordStateFiltered<never, IManagerFilters>; // fileManager.records is unused
  emailManager: IRecordStateFiltered<never, IManagerFilters>; // emailManager.records is unused
  schemaCollectionSummary: IRecordsState<ISchemaRecord>;
  connectionSettings: IRecordState<IConnectionSettingsRecord>;
  readOptionFields: IReadOptionsState;
  selectedConnectionInfo: ISelectedConnection | null;
  transactionSettingsDataChangeDetected: boolean;
  // should be null
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  selectedTemplateInfo: ISchemaRecord | {};
  // should be null
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  schemaCollectionAdded: ISchemaCollectionAddedState | {};
  isLoadCollectionClicked: boolean;
  schemaDocuments: IRecordsState<ISchemaDocumentRecord>;
  isUpdateSchemaClicked: boolean;
  // should be null
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  selectedDocumentInfo: ISchemaDocumentRecord | {};
  editSchemaTabValue: number;
  mapTabSelected: boolean;
  standardFilterTypes: IFilter[];
  schemaCollectionDocuments: ISchemaCollectionRecord[];
  schemaDocumentAdded: boolean;
  catalogSharingSetupDetails: ISharedCatalogRecord[];
  connectionPairObject: IConnectionPairBriefRecord[];
  connectionProfiles: IConnectionProfileState[];
  connectionProfilesLoaded: boolean;
}

interface ISetFilterPayload {
  value: IFilter[]
}
interface ISetEndpointSummaryFilterStatus {
  value: IEndpointStatusFilter[];
}
interface ISetManagerFilterStatusPayload {
  value: IManagerFilter[];
  heapIOIsInitValue: boolean;
}
const integrationSlice = createSlice({
  name: "integration",
  initialState: {
    tabSelections: {
      integration: 0,
      connections: 0,
      endpoints: 0,
      //disabled
      //newRequestConnection: 0,
      fileManager: 0,
    },
    requestNewConnection: { ...defaultNewConnection }, // accessed from the catalogGrid?
    editConnection: { ...defaultEditConnection },
    editSchema: { ...defaultEditSchema },
    // disabled
    // requestNewConnectionSummary: {
    //   records: [],
    //   recordsLoaded: false,
    //   error: null,
    // },
    connectionSummary: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
      filters: {
        status: [],
        type: [],
      },
      triggerRefresh: false,
      errorRates: [],
      loadingErrorRatesError: null,
      errorRatesLoaded: false,
    },
    endpointSummary: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
      filters: {
        status: [],
        type: [],
      },
      errorRates: [],
      loadingErrorRatesError: null,
      errorRatesLoaded: false,
    },
    connectionPair: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    fileManager: {
      records: [],
      recordsLoaded: false,
      loadingError: null, // unused, changing name to standardize
      filters: {
        status: [],
      },
    },
    emailManager: {
      records: [],
      recordsLoaded: false,
      loadingError: null,  // unused, changing name to standardize
      filters: {
        status: [],
      },
    },
    schemaCollectionSummary: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    connectionSettings: {
      record: null,
      recordLoaded: false,
      loadingError: null,
    },
    readOptionFields: {
      record: {
        ShipToQualifier: null,
        ShipFromQualifier: null,
        BillToQualifier: null,
        DTSegmentId: null,
        DTQualifierPosition: null,
        ShipDateQualifier: null,
        DeliveryDateQualifier: null,
        CancelDateQualifier: null,
        ReceiveDateQualifier: null,
      },
      recordLoaded: false,
      loadingError: null,
    },
    selectedConnectionInfo: null,
    transactionSettingsDataChangeDetected: false,
    selectedTemplateInfo: {},
    schemaCollectionAdded: {},
    isLoadCollectionClicked: false,
    schemaDocuments: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    isUpdateSchemaClicked: false,
    selectedDocumentInfo: {},
    editSchemaTabValue: 0,
    mapTabSelected: false,
    standardFilterTypes: [],
    schemaCollectionDocuments: [],
    schemaDocumentAdded: false,

    //shared catalog setup
    catalogSharingSetupDetails: [],
    connectionPairObject: [],
    connectionProfiles: [],
    connectionProfilesLoaded: false,
  } satisfies IIntegrationSliceState as IIntegrationSliceState,

  reducers: {
    setConnectionSummaryFilterTypes: (state, action: PayloadAction<ISetFilterPayload>) => {
      const { value } = action.payload;
      state.connectionSummary.filters.type = value;
    },
    setConnectionSummaryFilterStatus: (state, action: PayloadAction<ISetFilterPayload>) => {
      const { value } = action.payload;
      state.connectionSummary.filters.status = value;
    },

    // request new connection

    setNewConnectionModelURL: (state, action: PayloadAction<string>) => {
      state.requestNewConnection.model.url = action.payload;
    },
    setNewConnectionModelName: (state, action: PayloadAction<string>) => {
      state.requestNewConnection.model.name = action.payload;
    },
    setNewConnectionModelType: (state, action: PayloadAction<string>) => {
      state.requestNewConnection.model.type = action.payload;
    },
    setRequestNewConnectionSchemaConnectionNames: (state, action: PayloadAction<IConnectionRecord[]>) => {
      state.requestNewConnection.connectionNames.records = action.payload;
    },
    setRequestNewConnectionSchemaLibraryRecords: (state, action: PayloadAction<never[]>) => {
      state.requestNewConnection.schemaLibrary.records = action.payload;
    },
    setRequestNewConnectionSchemaLibrary: (state, action: PayloadAction<never[]>) => {
      state.requestNewConnection.model.schemaLibrary = action.payload;
    },
    setRequestNewConnectionWorkflows: (state, action: PayloadAction<never[]>) => {
      state.requestNewConnection.model.workflows = action.payload;
    },
    setRequestNewConnectionTargetConnections: (state, action: PayloadAction<never[]>) => {
      state.requestNewConnection.model.targetConnections = action.payload;
    },
    setRequestNewConnectionURL: (state, action: PayloadAction<string>) => {
      state.requestNewConnection.model.url = action.payload;
    },
    setEndpointSummaryFilterTypes: (state, action: PayloadAction<ISetFilterPayload>) => {
      const { value } = action.payload;
      state.endpointSummary.filters.type = value;
    },
    setStandardFilterTypes: (state, action: PayloadAction<ISetFilterPayload>) => {
      const { value } = action.payload;
      state.standardFilterTypes = value;
    },
    setEndpointSummaryFilterStatus: (state, action: PayloadAction<ISetEndpointSummaryFilterStatus>) => {
      const { value } = action.payload;
      state.endpointSummary.filters.status = value;
    },
    setFileManagerFilterStatus: (state, action: PayloadAction<ISetManagerFilterStatusPayload>) => {
      const { value } = action.payload;
      state.fileManager.filters.status = value;

      // heap.io

      if (action.payload && !action.payload.heapIOIsInitValue) {
        const selectedItems = action.payload.value
          .filter((item) => item.isChecked)
          .map((item) => item.Label);

        let filterText;

        if (selectedItems.length === action.payload.value.length) {
          filterText = "All";
        } else if (selectedItems.length === 0) {
          filterText = "None";
        } else {
          filterText = selectedItems.join(",");
        }

        const eventProps = {
          "Filter - Status": filterText,
        };

        window.heap.track("File Manager - Use Filter", eventProps);
      }
    },
    setEmailManagerFilterStatus: (state, action: PayloadAction<ISetManagerFilterStatusPayload>) => {
      const { value } = action.payload;
      state.emailManager.filters.status = value;
      // heap.io

      if (action.payload && !action.payload.heapIOIsInitValue) {
        const selectedItems = action.payload.value
          .filter((item) => item.isChecked)
          .map((item) => item.Label);

        let filterText;

        if (selectedItems.length === action.payload.value.length) {
          filterText = "All";
        } else if (selectedItems.length === 0) {
          filterText = "None";
        } else {
          filterText = selectedItems.join(",");
        }

        const eventProps = {
          "Filter - Status": filterText,
        };

        window.heap.track("Email Manager - Use Filter", eventProps);
      }
    },
    setEndpointSummary: (state, action: PayloadAction<{
      connectionSummary: IConnectionSummaryState;
      endpointSummary: IEndpointSummaryState;
    }>) => {
      const { connectionSummary, endpointSummary } = action.payload;
      state.connectionSummary = connectionSummary;
      state.endpointSummary = endpointSummary;
    },

    setSchemaLibraryFilterSearch: (state, action: PayloadAction<{
      value: string;
    }>) => {
      const { value } = action.payload;
      state.requestNewConnection.schemaLibrary.filters.search = value;
    },

    setTabSelectionsIntegration: (state, action: PayloadAction<number>) => {
      state.tabSelections.integration = action.payload;
    },
    setTabSelectionsConnections: (state, action: PayloadAction<number>) => {
      state.tabSelections.connections = action.payload;
    },
    setTabSelectionsEndpoints: (state, action: PayloadAction<number>) => {
      state.tabSelections.endpoints = action.payload;
    },
    setTabSelectionsFileManager: (state, action: PayloadAction<number>) => {
      state.tabSelections.fileManager = action.payload;
    },
    resetNewRequestConnection: (state) => {
      state.requestNewConnection = { ...defaultNewConnection };
    },
    setEditConnectionOpen: (state, action: PayloadAction<boolean>) => {
      state.editConnection.open = action.payload;
    },
    setEditConnectionId: (state, action: PayloadAction<number>) => {
      const newId = action.payload;
      if (newId !== state.editConnection.id) {
        state.editConnection = { ...defaultEditConnection };
        state.editConnection.id = newId;
      }
    },
    setEditConnectionSelectedPairs: (state, action: PayloadAction<never[]>) => {
      state.editConnection.selectedPairs = action.payload;
    },
    setEditConnectionConnectionPairWorkflow: (state, action: PayloadAction<IConnectionPairWorkflowState>) => {
      state.editConnection.connectionPairWorkflow = action.payload;
    },
    setEditSchemaOpen: (state, action: PayloadAction<boolean>) => {
      state.editSchema.open = action.payload;
    },
    setSelectedConnection: (state, action: PayloadAction<{
      selectedConnectionInfo: ISelectedConnection | null;
    }>) => {
      const { selectedConnectionInfo } = action.payload;
      state.selectedConnectionInfo = selectedConnectionInfo;
    },
    setTransactionSettingsDataChangeDetected: (state, action: PayloadAction<{
      transactionSettingsDataChangeDetected: boolean
    }>) => {
      const { transactionSettingsDataChangeDetected } = action.payload;
      state.transactionSettingsDataChangeDetected =
        transactionSettingsDataChangeDetected;
    },
    setSelectedTemplateInfo: (state, action: PayloadAction<{
      selectedTemplateInfo: ISchemaRecord;
    }>) => {
      const { selectedTemplateInfo } = action.payload;
      state.selectedTemplateInfo = selectedTemplateInfo;
    },
    setSchemaCollectionAdded: (state, action: PayloadAction<{
      schemaCollectionAdded: ISchemaCollectionAddedState;
    }>) => {
      const { schemaCollectionAdded } = action.payload;
      state.schemaCollectionAdded = schemaCollectionAdded;
    },
    setIsLoadCollectionClicked: (state, action: PayloadAction<{
      isLoadCollectionClicked: boolean;
    }>) => {
      const { isLoadCollectionClicked } = action.payload;
      state.isLoadCollectionClicked = isLoadCollectionClicked;
    },
    setIsUpdateSchemaClicked: (state, action: PayloadAction<{
      isUpdateSchemaClicked: boolean;
    }>) => {
      const { isUpdateSchemaClicked } = action.payload;
      state.isUpdateSchemaClicked = isUpdateSchemaClicked;
    },
    setSelectedDocumentInfo: (state, action: PayloadAction<{
      selectedDocumentInfo: ISchemaDocumentRecord;
    }>) => {
      const { selectedDocumentInfo } = action.payload;
      state.selectedDocumentInfo = selectedDocumentInfo;
    },
    setEditSchemaTabValue: (state, action: PayloadAction<number>) => {
      state.editSchemaTabValue = action.payload;
    },
    setMapTabSelected: (state, action: PayloadAction<boolean>) => {
      state.mapTabSelected = action.payload;
    },
    setTriggerRefreshForConnectionSummary: (state) => {
      state.connectionSummary.triggerRefresh =
        !state.connectionSummary.triggerRefresh;
    },
    setSchemaCollectionDocuments: (state, action: PayloadAction<{
      schemaCollectionDocuments: ISchemaCollectionRecord[];
    }>) => {
      const { schemaCollectionDocuments } = action.payload;
      state.schemaCollectionDocuments = schemaCollectionDocuments;
    },
    setSchemaDocumentAdded: (state, action: PayloadAction<{
      schemaDocumentAdded: boolean;
    }>) => {
      const { schemaDocumentAdded } = action.payload;
      state.schemaDocumentAdded = schemaDocumentAdded;
    },
    //shared catalog setup
    setSharedCatalogSetupDetails: (state, action: PayloadAction<{
      catalogSharingSetupDetails: ISharedCatalogRecord;
    }>) => {
      const { catalogSharingSetupDetails } = action.payload;
      state.catalogSharingSetupDetails.push(catalogSharingSetupDetails);
    },
    clearSharedCatalogSetupDetails: (state, action: PayloadAction<{
      catalogSharingSetupDetails: ISharedCatalogRecord[];
    }>) => {
      const { catalogSharingSetupDetails } = action.payload;
      state.catalogSharingSetupDetails = catalogSharingSetupDetails;
    },
    setConnectionPairObject: (state, action: PayloadAction<{
      connectionPairObject: IConnectionPairBriefRecord;
    }>) => {
      const { connectionPairObject } = action.payload;
      state.connectionPairObject.push(connectionPairObject);
    },
    clearConnectionPairObject: (state, action: PayloadAction<{
      connectionPairObject: IConnectionPairBriefRecord[];
    }>) => {
      const { connectionPairObject } = action.payload;
      state.connectionPairObject = connectionPairObject;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionSummaryErrorRates.pending, (state) => {
        state.connectionSummary.errorRatesLoaded = false;
      })
      .addCase(fetchConnectionSummaryErrorRates.rejected, (state, action) => {
        state.connectionSummary.errorRatesLoaded = true;
        state.connectionSummary.loadingErrorRatesError = action.error.message ?? null;
      })
      .addCase(fetchConnectionSummaryErrorRates.fulfilled, (state, action) => {
        state.connectionSummary.errorRatesLoaded = true;
        const result: IConnectionSummaryErrorRatesByConnectionId = {};
        action.payload.forEach(errorRate => result[errorRate.ConnectionId] = errorRate);

        state.connectionSummary.errorRates = result;
      })
      .addCase(fetchConnectionSummaries.pending, (state) => {
        state.connectionSummary.recordsLoaded = false;
      })
      .addCase(fetchConnectionSummaries.rejected, (state, action) => {
        state.connectionSummary.recordsLoaded = true;
        state.connectionSummary.loadingError = action.error.message ?? null;
      })
      .addCase(fetchConnectionSummaries.fulfilled, (state, action) => {
        state.connectionSummary.recordsLoaded = true;
        state.connectionSummary.records = action.payload;
      })
      .addCase(fetchEndpointErrorRates.pending, (state) => {
        state.endpointSummary.errorRatesLoaded = false;
      })
      .addCase(fetchEndpointErrorRates.rejected, (state, action) => {
        state.endpointSummary.errorRatesLoaded = true;
        state.endpointSummary.loadingErrorRatesError = action.error.message ?? null;
      })
      .addCase(fetchEndpointErrorRates.fulfilled, (state, action) => {
        state.endpointSummary.errorRatesLoaded = true;
        const result: IEndpointErrorRatesByEndpointId = {};
        action.payload.forEach(errorRate => result[errorRate.EndpointId] = errorRate);

        state.endpointSummary.errorRates = result;
      })
      .addCase(fetchEndpointSummaries.rejected, (state, action) => {
        state.endpointSummary.recordsLoaded = true;
        state.endpointSummary.loadingError = action.error.message ?? null;
      })
      .addCase(fetchEndpointSummaries.fulfilled, (state, action) => {
        state.endpointSummary.recordsLoaded = true;
        state.endpointSummary.records = action.payload;
      })
      .addCase(fetchSchemaLibraries.pending, (state) => {
        state.requestNewConnection.schemaLibrary.recordsLoaded = false;
      })
      .addCase(fetchSchemaLibraries.rejected, (state, action) => {
        state.requestNewConnection.schemaLibrary.recordsLoaded = true;
        state.requestNewConnection.schemaLibrary.loadingError =
          action.error.message ?? null;
      })
      .addCase(fetchSchemaLibraries.fulfilled, (state, action) => {
        state.requestNewConnection.schemaLibrary.recordsLoaded = true; // standardize naming convention
        state.requestNewConnection.schemaLibrary.records = action.payload;
      })
      .addCase(fetchConnectionNames.pending, (state) => {
        state.requestNewConnection.connectionNames.recordsLoaded = false;
      })
      .addCase(fetchConnectionNames.rejected, (state, action) => {
        state.requestNewConnection.connectionNames.recordsLoaded = true;
        state.requestNewConnection.connectionNames.loadingError =
          action.error.message ?? null;
      })
      .addCase(fetchConnectionNames.fulfilled, (state, action) => {
        state.requestNewConnection.connectionNames.recordsLoaded = true;
        state.requestNewConnection.connectionNames.records = action.payload;
      })

      .addCase(fetchConnectionIds.pending, (state) => {
        state.editConnection.allConnectionsLoaded = false;
      })
      .addCase(fetchConnectionIds.rejected, (state, action) => {
        state.editConnection.allConnectionsLoaded = true;
        state.editConnection.loadingError = action.error.message ?? null;
      })
      .addCase(fetchConnectionIds.fulfilled, (state, action) => {
        state.editConnection.allConnectionsLoaded = true;
        const parsedRecords = action.payload.map((x) => {
          const json = JSON.parse(x.JSONProperties);
          let typeLabel = "";
          if (json) {
            const label = connectionTypes.filter(
              (x) => x.key == json.ConnectionType,
            )[0];
            if (label) {
              typeLabel = label.label;
            }
          }
          return { ...x, ...json, id: x.ConnectionId, typeLabel: typeLabel };
        });
        state.editConnection.allConnections = parsedRecords;
      })

      .addCase(fetchEndpoints.pending, (state) => {
        state.editConnection.endpointsLoaded = false;
      })
      .addCase(fetchEndpoints.rejected, (state, action) => {
        state.editConnection.endpointsLoaded = true;
        state.editConnection.loadingError = action.error.message?? null;
      })
      .addCase(fetchEndpoints.fulfilled, (state, action) => {
        state.editConnection.endpointsLoaded = true;
        state.editConnection.endpoints = action.payload;
      })

      .addCase(fetchConnectionAllPairs.pending, (state) => {
        state.editConnection.connectionPairsLoaded = false;
      })
      .addCase(fetchConnectionAllPairs.rejected, (state, action) => {
        state.editConnection.connectionPairsLoaded = true;
        state.editConnection.loadingError = action.error.message ?? null;
      })
      .addCase(fetchConnectionAllPairs.fulfilled, (state, action) => {
        state.editConnection.connectionPairsLoaded = true;
        state.editConnection.connectionPairs = action.payload;
      })
      .addCase(fetchConnectionProfiles.pending, (state) => {
        state.editConnection.connectionProfilesLoaded = false;
      })
      .addCase(fetchConnectionProfiles.rejected, (state, action) => {
        state.editConnection.connectionProfilesLoaded = true;
        state.editConnection.connectionProfilesError = action.error.message ?? null;
      })
      .addCase(fetchConnectionProfiles.fulfilled, (state, action) => {
        state.editConnection.connectionProfilesLoaded = true;
        state.editConnection.connectionProfiles = action.payload;
      })

      .addCase(fetchOperations.pending, (state) => {
        state.editConnection.operationsLoaded = false;
      })
      .addCase(fetchOperations.rejected, (state, action) => {
        state.editConnection.operationsLoaded = true;
        state.editConnection.operationsError = action.error.message ?? null;
      })
      .addCase(fetchOperations.fulfilled, (state, action) => {
        state.editConnection.operationsLoaded = true;
        state.editConnection.operations = action.payload;
      })
      .addCase(fetchConnectionPairs.pending, (state) => {
        state.connectionPair.recordsLoaded = false;
      })
      .addCase(fetchConnectionPairs.rejected, (state, action) => {
        state.connectionPair.recordsLoaded = true;
        state.connectionPair.loadingError = action.error.message ?? null;
      })
      .addCase(fetchConnectionPairs.fulfilled, (state, action) => {
        state.connectionPair.recordsLoaded = true;
        state.connectionPair.records = action.payload;
      })
      .addCase(fetchTemplates.pending, (state) => {
        state.schemaCollectionSummary.recordsLoaded = false;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.schemaCollectionSummary.recordsLoaded = true;
        state.schemaCollectionSummary.loadingError = action.error.message ?? null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.schemaCollectionSummary.recordsLoaded = true;
        state.schemaCollectionSummary.records = action.payload;
      })
      .addCase(fetchConnectionSettings.pending, (state) => {
        state.connectionSettings.recordLoaded = false;
      })
      .addCase(fetchConnectionSettings.rejected, (state, action) => {
        state.connectionSettings.recordLoaded = true;
        state.connectionSettings.loadingError = action.error.message ?? null;
      })
      .addCase(fetchConnectionSettings.fulfilled, (state, action) => {
        state.connectionSettings.recordLoaded = true;
        state.connectionSettings.record = {
          ...action.payload.IsaGsMap,
          ...action.payload.AcknowledgementSettings,
          IsISAGSOptionalFieldRequired:
            action.payload.IsISAGSOptionalFieldRequired,
          TestCustomConnectionMatch: action.payload.TestCustomConnectionMatch,
          ProdCustomConnectionMatch: action.payload.ProdCustomConnectionMatch,
        };
      })
      .addCase(fetchReadOptionFields.pending, (state) => {
        state.readOptionFields.recordLoaded = false;
      })
      .addCase(fetchReadOptionFields.rejected, (state, action) => {
        state.readOptionFields.recordLoaded = true;
        state.readOptionFields.loadingError = action.error.message ?? null;
      })
      .addCase(fetchReadOptionFields.fulfilled, (state, action) => {
        state.readOptionFields.recordLoaded = true;
        state.readOptionFields.record = action.payload;
      })
      .addCase(updateSchema.pending, (state) => {
        state.schemaDocuments.recordsLoaded = false;
      })
      .addCase(updateSchema.rejected, (state, action) => {
        state.schemaDocuments.recordsLoaded = true;
        state.schemaDocuments.loadingError = action.error.message ?? null;
      })
      .addCase(updateSchema.fulfilled, (state, action) => {
        state.schemaDocuments.recordsLoaded = true;
        state.schemaDocuments.records = action.payload;
      });
  },
});

export const fetchOperations = createAsyncThunk(
  "connection-operations",
  async (params) => {
    const response = await axios.get("/connections/operations", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionProfiles = createAsyncThunk(
  "connection-profile-all",
  async (params) => {
    const response = await axios.get("/connections/connection-profile-all", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionAllPairs = createAsyncThunk(
  "connectionpair",
  async (params) => {
    const response = await axios.get("/connectionpair", {
      params: params,
    });
    return response.data;
  },
);

interface IFetchConnectionIdsResponse {
  ConnectionId: number;
  ConnectionName: string;
  EndpointId: number;
  SchemaCollectionId?: number;
  IsDisabled: boolean;
  JSONProperties: string;
}

export const fetchConnectionIds = createAsyncThunk(
  "connectionpair/ids",
  async () => {
    const response = await axios.get<IFetchConnectionIdsResponse[]>("/connectionpair/ids");
    return response.data;
  },
);

export const fetchEndpoints = createAsyncThunk("endpoint", async (params) => {
  const response = await axios.get("/endpoint", {
    params: params,
  });
  return response.data;
});

export interface IEndpointErrorRate {
  EndpointId: number;
  TotalAttempts: number;
  FailedAttempts: number;
}
export interface IConnectionSummaryErrorRate {
  ConnectionId: number;
  TotalAttempts: number;
  FailedAttempts: number;
}
export interface IFetchConnectionSummariesAction {
    statusFilterJson: string;
    typeFilterJson: string;
    search: string;
    displayType: any;
}

export const fetchConnectionSummaryErrorRates = createAsyncThunk<IConnectionSummaryErrorRate[], void>(
  "connections/connection-summary-error-rate",
  async () => {
    const response = await axios.get<IConnectionSummaryErrorRate[]>("/connections/connection-summary/error-rates")
    return response.data;
  }
)

export const fetchConnectionSummaries = createAsyncThunk<IConnectionSummaryRecord[], IFetchConnectionSummariesAction | null>(
  "connections/connection-summary",
  async (params, { dispatch }) => {
    const response = await axios.get("/connections/connection-summary", {
      params: params,
    });

    dispatch(fetchConnectionSummaryErrorRates());
    return response.data;
  },
);

export const fetchSchemaLibraries = createAsyncThunk(
  "connections/schema-library",
  async (params) => {
    const response = await axios.get("/connections/schema-library", {
      params: params,
    });
    return response.data;
  },
);

export const fetchEndpointStatus = createAsyncThunk(
  "endpoint/endpoint-status",
  async (params) => {
    const response = await axios.get("/endpoint/endpoint-status", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionNames = createAsyncThunk(
  "connections/connection-name",
  async (params) => {
    const response = await axios.get("/connections/connection-name", {
      params: params,
    });
    return response.data;
  },
);

interface IFetchEndpointSummariesAction {
    statusFilterJson: string;
    typeFilterJson: string;
    search: string;
    startDate: any;
    endDate: any;
}

export const fetchEndpointErrorRates = createAsyncThunk<IEndpointErrorRate[], void>(
  "endpoint/endpoint-manager-error-rates",
  async () => {
    const response = await axios.get<IEndpointErrorRate[]>("/endpoint/endpoint-manager/error-rates");
    return response.data;
  }
)
export const fetchEndpointSummaries = createAsyncThunk<IEndpointRecord[], IFetchEndpointSummariesAction>(
  "endpoint/endpoint-manager",
  async (params, { dispatch }) => {
    const response = await axios.get<IEndpointRecord[]>("/endpoint/endpoint-manager", {
      params: params,
    });
    dispatch(fetchEndpointErrorRates());
    return response.data;
  },
);

export const fetchConnectionPairs = createAsyncThunk(
  "integrations/fetchConnectionPairs",
  async (params) => {
    const response = await axios.get("/connectionpair/connection-pairs", {
      params: params,
    });
    return response.data;
  },
);

export const fetchTemplates = createAsyncThunk(
  "integration/fetchTemplates",
  async ({ filter }: {
    filter: string;
  }) => {
    const response = await axios.get("/template", {
      params: { filter: filter },
    });
    return response.data;
  },
);

export const fetchConnectionSettings = createAsyncThunk(
  "integration/fetchConnectionSettings",
  async ({ connectionId, artifactType, connectionArtifactSchemaId } : {
    connectionId: number;
    artifactType: number;
    connectionArtifactSchemaId: string | null;
  }) => {
    const response = await axios.get(
      "/connectionartifactschema/connection-settings",
      {
        params: {
          connectionId: connectionId,
          artifactType: artifactType,
          connectionArtifactSchemaId: connectionArtifactSchemaId,
        },
      },
    );
    return response.data;
  },
);


export const fetchReadOptionFields = createAsyncThunk(
  "integration/fetchReadOptionFields",
  async ({ connectionId, artifactType } : {
    connectionId: number;
    artifactType: number;
  }) => {
    const response = await axios.get(
      "/connectionartifactschema/readoption-fields",
      {
        params: { connectionId: connectionId, artifactType: artifactType },
      },
    );
    return response.data.values;
  },
);

export const loadSchemaCollection = createAsyncThunk(
  "integration/loadSchemaCollection",
  async ({ templateId, connectionId }: {
    templateId: string;
    connectionId: number;
  }) => {
    const response = await axios.post(
      "/template/load-schema-collection",
      null,
      {
        params: {
          templateId: templateId,
          connectionId: connectionId,
        },
      },
    );
    return response;
  },
);

export const updateSchema = createAsyncThunk(
  "integration/updateSchema",
  async ({ schemaCollectionId, connectionId }: {
    schemaCollectionId: string;
    connectionId: number;
  }) => {
    const response = await axios.get("/template/endpoint-schema", {
      params: {
        schemaCollectionId: schemaCollectionId,
        connectionId: connectionId,
      },
    });
    return response.data;
  },
);

export const saveSchema = createAsyncThunk(
  "integration/saveSchema",
  async ({
    endpointSchemaId,
    connectionId,
    artifactType
  } : {
    endpointSchemaId: string;
    connectionId: number;
    artifactType: number;
  }) => {
    const response = await axios.post("/template/endpoint-schema", null, {
      params: {
        endpointSchemaId: endpointSchemaId,
        connectionId: connectionId,
        artifactType: artifactType,
      },
    });
    return response;
  },
);

export const removeSchemaCollection = createAsyncThunk(
  "integration/removeSchemaCollection",
  async ({ connectionId, schemaCollectionId } : {
    connectionId: number;
    schemaCollectionId: string;
  }) => {
    const response = await axios.put(
      "/template/remove-schema-collection",
      null,
      {
        params: {
          connectionId: connectionId,
          schemaCollectionId: schemaCollectionId,
        },
      },
    );
    return response;
  },
);

export const updateSchemaCollection = createAsyncThunk(
  "integration/updateSchemaCollection",
  async ({ connectionId, schemaCollectionId }: {
    connectionId: number;
    schemaCollectionId: string;
  }) => {
    const response = await axios.put(
      "/template/update-schema-collection",
      null,
      {
        params: {
          connectionId: connectionId,
          schemaCollectionId: schemaCollectionId,
        },
      },
    );
    return response;
  },
);

export const selectAllConnectionSummaries = (state: RootState) =>
  state.tempInfo.integration.connectionSummary.records;
export const selectConnectionSummaryFilterTypes = (state: RootState) =>
  state.tempInfo.integration.connectionSummary.filters.type;
export const selectConnectionSummaryFilterStatus = (state: RootState) =>
  state.tempInfo.integration.connectionSummary.filters.status;
export const selectConnectionSummaryRecordLoaded = (state: RootState) =>
  state.tempInfo.integration.connectionSummary.recordsLoaded;
export const selectConnectionSummaryRecords = (state: RootState) =>
  state.tempInfo.integration.connectionSummary.records;
export const selectNewConnectionModelTargetConnections = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model.targetConnections;
export const selectConnectionNamesRecords = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.connectionNames.records;
export const selectConnectionNamesLoaded = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.connectionNames.recordsLoaded;
export const selectNewConnectionModelWorkflows = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model.workflows;
export const selectNewConnectionModelSchemaLibrary = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model.schemaLibrary;
export const selectSchemaLibraryRecords = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.schemaLibrary.records;
export const selectSchemaLibraryLoaded = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.schemaLibrary.recordsLoaded;
export const selectSchemaLibraryFilterSearch = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.schemaLibrary.filters.search;
export const selectEndpointSummaryFilterTypes = (state: RootState) =>
  state.tempInfo.integration.endpointSummary.filters.type;
export const selectEndpointSummaryFilterStatus = (state: RootState) =>
  state.tempInfo.integration.endpointSummary.filters.status;
export const selectEndpointSummaryRecordLoaded = (state: RootState) =>
  state.tempInfo.integration.endpointSummary.recordsLoaded;
export const selectEndpointSummaryRecords = (state: RootState) =>
  state.tempInfo.integration.endpointSummary.records;
export const selectNewConnectionModelName = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model.name;
export const selectNewConnectionModelType = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model.type;
export const selectNewConnectionModelURL = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model.url;
export const selectNewConnectionModel = (state: RootState) =>
  state.tempInfo.integration.requestNewConnection.model;
export const selectTabSelectionsIntegration = (state: RootState) =>
  state.tempInfo.integration.tabSelections.integration;
export const selectTabSelectionsConnections = (state: RootState) =>
  state.tempInfo.integration.tabSelections.connections;
export const selectTabSelectionsEndpoints = (state: RootState) =>
  state.tempInfo.integration.tabSelections.endpoints;
export const selectTabselectionsFileManager = (state: RootState) =>
  state.tempInfo.integration.tabSelections.fileManager;
export const selectEditConnectionOpen = (state: RootState) =>
    state.tempInfo.integration.editConnection.open;
export const selectConnectionModel = (state: RootState) =>
    state.tempInfo.integration.editConnection.model;
export const selectEditConnectionId = (state: RootState) =>
  state.tempInfo.integration.editConnection.id;
export const selectEditConnectionAllConnections = (state: RootState) =>
  state.tempInfo.integration.editConnection.allConnections;
export const selectEditConnectionEndpoints = (state: RootState) =>
  state.tempInfo.integration.editConnection.endpoints;
export const selectEditConnectionEndpointsLoaded = (state: RootState) =>
  state.tempInfo.integration.editConnection.endpointsLoaded;
export const selectEditConnectionConnectionPairs = (state: RootState) =>
  state.tempInfo.integration.editConnection.connectionPairs;
export const selectEditConnectionConnectionPairsLoaded = (state: RootState) =>
  state.tempInfo.integration.editConnection.connectionPairsLoaded;
export const selectEditConnectionconnectionProfilesLoaded = (state: RootState) =>
  state.tempInfo.integration.editConnection.connectionProfilesLoaded;
export const selectEditConnectionConnectionProfiles = (state: RootState) =>
  state.tempInfo.integration.editConnection.connectionProfiles;
export const selectEditConnectionOperations = (state: RootState) =>
  state.tempInfo.integration.editConnection.operations;
export const selectEditConnectionConnectionPairWorkflow = (state: RootState) =>
  state.tempInfo.integration.editConnection.connectionPairWorkflow;
export const selectEditConnectionSelectedPairs = (state: RootState) =>
    state.tempInfo.integration.editConnection.selectedPairs;
export const selectEditConnectionModifiedConnectionPairs = (state: RootState) =>
    state.tempInfo.integration.editConnection.modifiedConnectionPairs;
export const selectFileManagerFilterStatus = (state: RootState) =>
  state.tempInfo.integration.fileManager.filters.status;
export const selectFileManagerFilesLoaded = (state: RootState) =>
  state.tempInfo.integration.fileManager.recordsLoaded;
export const selectFileManagerRecords = (state: RootState) =>
  state.tempInfo.integration.fileManager.records;
export const selectEmailManagerFilterStatus = (state: RootState) =>
  state.tempInfo.integration.emailManager.filters.status;
export const selectEditSchemaOpen = (state: RootState) =>
  state.tempInfo.integration.editSchema.open;
export const selectSchemaCollectionRecords = (state: RootState) =>
  state.tempInfo.integration.schemaCollectionSummary.records;
export const selectConnectionSettings = (state: RootState) =>
  state.tempInfo.integration.connectionSettings.record;
export const selectReadOptionFields = (state: RootState) =>
  state.tempInfo.integration.readOptionFields.record;
export const selectSelectedConnection = (state: RootState) =>
  state.tempInfo.integration.selectedConnectionInfo;
export const selectTransactionSettingsDataChangeDetected = (state: RootState) =>
  state.tempInfo.integration.transactionSettingsDataChangeDetected;
export const selectSchemaCollectionAdded = (state: RootState) =>
  state.tempInfo.integration.schemaCollectionAdded;
export const selectIsLoadCollectionClicked = (state: RootState) =>
  state.tempInfo.integration.isLoadCollectionClicked;
export const selectSchemaDocuments = (state: RootState) =>
  state.tempInfo.integration.schemaDocuments.records;
export const selectUpdateSchemaClicked = (state: RootState) =>
  state.tempInfo.integration.isUpdateSchemaClicked;
export const selectSelectedTemplateInfo = (state: RootState) =>
  state.tempInfo.integration.selectedTemplateInfo;
export const selectSelectedDocumentInfo = (state: RootState) =>
  state.tempInfo.integration.selectedDocumentInfo;

export const selectEditSchemaTabValue = (state: RootState) =>
  state.tempInfo.integration.editSchemaTabValue;
export const selectMapTabSelected = (state: RootState) =>
  state.tempInfo.integration.mapTabSelected;
export const selectStandardFilterTypes = (state: RootState) =>
  state.tempInfo.integration.standardFilterTypes;
export const selectSchemaCollectionInfo = (state: RootState) =>
  state.tempInfo.integration.schemaCollectionDocuments;
export const selectSchemaDocumentAdded = (state: RootState) =>
  state.tempInfo.integration.schemaDocumentAdded;

// disabled selection
// export const selectRequestedNewConnections = (state: RootState) =>
//   state.tempInfo.integration.requestNewConnectionSummary.records;
// export const selectRequestedNewConnectionsSummaryRecordLoaded = (state: RootState) =>
//   state.tempInfo.integration.requestNewConnectionSummary.recordsLoaded;

export const selectConnectionPairs = (state: RootState) =>
  state.tempInfo.integration.connectionPair.records;
export const selectConnectionPairsRecordLoaded = (state: RootState) =>
  state.tempInfo.integration.connectionPair.recordsLoaded;
export const selectTriggerRefreshForConnectionSummary = (state: RootState) =>
  state.tempInfo.integration.connectionSummary.triggerRefresh;
export const selectConnectionErrorRates = (state: RootState) => 
  state.tempInfo.integration.connectionSummary.errorRates;
export const selectEndpointErrorRates = (state: RootState) =>
  state.tempInfo.integration.endpointSummary.errorRates;
//shared catalog setup
export const selectCatalogSharingSetupDetails = (state: RootState) =>
  state.tempInfo.integration.catalogSharingSetupDetails;
export const selectConnectionPairObject = (state: RootState) =>
  state.tempInfo.integration.connectionPairObject;

export const {
  setConnectionSummaryFilterTypes,
  setConnectionSummaryFilterStatus,
  setSchemaLibraryFilterSearch,
  setRequestNewConnectionSchemaLibrary,
  setRequestNewConnectionTargetConnections,
  setRequestNewConnectionURL,
  setEndpointSummaryFilterTypes,
  setEndpointSummaryFilterStatus,
  setFileManagerFilterStatus,
  setEmailManagerFilterStatus,
  setEndpointSummary,
  setRequestNewConnectionSchemaLibraryRecords,
  setRequestNewConnectionWorkflows,
  setRequestNewConnectionSchemaConnectionNames,
  setNewConnectionModelName,
  setNewConnectionModelType,
  setNewConnectionModelURL,
  setTabSelectionsIntegration,
  setTabSelectionsConnections,
  setTabSelectionsEndpoints,
  setTabSelectionsFileManager,
  resetNewRequestConnection,
  setEditConnectionOpen,
  setEditConnectionId,
  setEditSchemaOpen,
  setSelectedConnection,
  setTransactionSettingsDataChangeDetected,
  setSelectedTemplateInfo,
  setSchemaCollectionAdded,
  setIsLoadCollectionClicked,
  setIsUpdateSchemaClicked,
  setSelectedDocumentInfo,
  setEditSchemaTabValue,
  setMapTabSelected,
  setStandardFilterTypes,
  setEditConnectionSelectedPairs,
  setEditConnectionConnectionPairWorkflow,
  setSchemaCollectionDocuments,
  setTriggerRefreshForConnectionSummary,
  setSchemaDocumentAdded,
  setSharedCatalogSetupDetails,
  clearSharedCatalogSetupDetails,
  setConnectionPairObject,
  clearConnectionPairObject,
} = integrationSlice.actions;
export default integrationSlice.reducer;
